import { useState, useMemo } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import {
  Scissors, Plus, TrendingUp, TrendingDown, BarChart2, RefreshCw,
  Calendar, Pencil, Trash2, Tags, Check, X,
  Settings, ChevronUp, ChevronDown, Receipt, Download, History,
  Lock, LockOpen,
} from "lucide-react";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Modal } from "../../components/ui/Modal";
import { LoadingSpinner } from "../../components/ui/LoadingSpinner";
import { EmptyState } from "../../components/ui/EmptyState";
import { useToast } from "../../hooks/useToast";
import { ToastContainer } from "../../components/ui/Toast";
import {
  useStoreServices,
  useCreateStoreService,
  useUpdateStoreService,
  useDeleteStoreService,
  useStoreRecords,
  useStoreRecordsInfinite,
  useStoreStaff,
  useCreateStoreRecord,
  useUpdateStoreRecord,
  useDeleteStoreRecord,
  useStoreExpensesInfinite,
  useCreateStoreExpense,
  useUpdateStoreExpense,
  useDeleteStoreExpense,
  useStoreIncomeInfinite,
  useCreateStoreIncome,
  useUpdateStoreIncome,
  useDeleteStoreIncome,
  useStaffWalletBalances,
  useStoreConfig,
  useSaveStoreConfig,
} from "../../hooks/useStore";
import { storeApi } from "../../lib/storeApi";
import type { StoreRecord, StoreService, StoreExpense, StoreIncome, StoreConfigCategory } from "../../lib/storeApi";
import type { ServiceRequest as ServiceRequestItem } from "../../lib/walletApi";
import { formatCurrency, formatDate } from "../../utils/format";
import { useAuth } from "../../hooks/useAuth";
import { useAdminServiceRequests, useApproveServiceRequest, useRejectServiceRequest } from "../../hooks/useWallet";
import { AuditHistoryModal } from "../../components/AuditHistoryModal";
import { auditApi } from "../../lib/auditApi";

// ── Constants ─────────────────────────────────────────────────────────────────

const COLORS = ["#2447d9", "#4263eb", "#748ffc", "#91a7ff", "#bac8ff"];

// ── Helpers ───────────────────────────────────────────────────────────────────

function todayIso() { return new Date().toISOString().slice(0, 10); }
function newId() { return crypto.randomUUID(); }
function currentMonth() { return new Date().toISOString().slice(0, 7); }
function currentYear() { return new Date().getFullYear().toString(); }

function formatPrefixLabel(prefix: string) {
  if (/^\d{4}-\d{2}-\d{2}$/.test(prefix)) return `Day: ${prefix}`;
  if (/^\d{4}-\d{2}$/.test(prefix)) {
    const [y, m] = prefix.split("-");
    const d = new Date(parseInt(y!), parseInt(m!) - 1, 1);
    return `Month: ${d.toLocaleDateString("en-PH", { month: "long", year: "numeric" })}`;
  }
  return `Year: ${prefix}`;
}

const inputClass = "w-full rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#2447d9]";

// ── Service Row ───────────────────────────────────────────────────────────────

function ServiceRow({
  service,
  storeId,
}: {
  service: StoreService;
  storeId: string;
}) {
  const updateService = useUpdateStoreService();
  const deleteService = useDeleteStoreService();
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(service.name);
  const [editAmount, setEditAmount] = useState(String(service.serviceAmount ?? ""));
  const [editCommType, setEditCommType] = useState<"percentage" | "fixed">(service.commissionType ?? "percentage");
  const [editRate, setEditRate] = useState(String(service.commissionRate ?? 0));
  const [editFixed, setEditFixed] = useState(String(service.commissionFixed ?? 0));
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleToggleLock = async () => {
    try {
      await updateService.mutateAsync({
        serviceId: service.serviceId,
        payload: {
          storeId,
          name: service.name,
          serviceAmount: service.serviceAmount,
          commissionRate: service.commissionRate,
          commissionType: service.commissionType ?? "percentage",
          commissionFixed: service.commissionFixed,
          lockAmount: !service.lockAmount,
        },
      });
    } catch { /* ignore */ }
  };

  const resetEdit = () => {
    setEditName(service.name);
    setEditAmount(String(service.serviceAmount ?? ""));
    setEditCommType(service.commissionType ?? "percentage");
    setEditRate(String(service.commissionRate ?? 0));
    setEditFixed(String(service.commissionFixed ?? 0));
    setEditing(false);
  };

  const handleSaveEdit = async () => {
    if (!editName.trim()) return;
    const payload: { storeId: string; name: string; serviceAmount?: number; commissionRate?: number; commissionType: "percentage" | "fixed"; commissionFixed?: number; lockAmount?: boolean } = {
      storeId,
      name: editName.trim(),
      commissionType: editCommType,
      lockAmount: service.lockAmount,
    };
    if (editAmount !== "") {
      const amt = parseFloat(editAmount);
      if (!isNaN(amt) && amt >= 0) payload.serviceAmount = amt;
    }
    if (editCommType === "percentage") {
      const rate = parseFloat(editRate);
      if (isNaN(rate) || rate < 0 || rate > 100) return;
      payload.commissionRate = rate;
    } else {
      const fixed = parseFloat(editFixed);
      if (isNaN(fixed) || fixed < 0) return;
      payload.commissionFixed = fixed;
    }
    try {
      await updateService.mutateAsync({ serviceId: service.serviceId, payload });
      setEditing(false);
    } catch {
      // keep editing open on error
    }
  };

  const handleDelete = async () => {
    try {
      await deleteService.mutateAsync({ serviceId: service.serviceId, storeId });
    } catch {
      setConfirmDelete(false);
    }
  };

  if (editing) {
    return (
      <div className="py-2 border-b border-[#f1f5f9] last:border-0 space-y-1.5">
        <input
          autoFocus
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Escape") resetEdit(); }}
          placeholder="Service name"
          className="w-full rounded-lg border border-[#2447d9] bg-white px-2 py-1.5 text-sm focus:outline-none"
        />
        <div className="relative">
          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-[#94a3b8]">₱</span>
          <input
            type="number" min="0" step="0.01"
            value={editAmount}
            onChange={(e) => setEditAmount(e.target.value)}
            placeholder="Service price (optional)"
            className="w-full rounded-lg border border-[#e2e8f0] bg-white px-2 py-1.5 pl-5 text-sm focus:outline-none focus:ring-1 focus:ring-[#2447d9]"
          />
        </div>
        <div className="flex flex-wrap gap-1.5 items-center">
          <select
            value={editCommType}
            onChange={(e) => setEditCommType(e.target.value as "percentage" | "fixed")}
            className="rounded-lg border border-[#e2e8f0] bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#2447d9]"
          >
            <option value="percentage">% Rate</option>
            <option value="fixed">Fixed ₱</option>
          </select>
          <div className="relative flex-1 min-w-[80px]">
            {editCommType === "percentage" ? (
              <>
                <input
                  type="number" min="0" max="100" step="0.5"
                  value={editRate}
                  onChange={(e) => setEditRate(e.target.value)}
                  className="w-full rounded-lg border border-[#e2e8f0] bg-white px-2 py-1.5 pr-6 text-sm focus:outline-none focus:ring-1 focus:ring-[#2447d9]"
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-[#94a3b8]">%</span>
              </>
            ) : (
              <>
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-[#94a3b8]">₱</span>
                <input
                  type="number" min="0" step="0.01"
                  value={editFixed}
                  onChange={(e) => setEditFixed(e.target.value)}
                  className="w-full rounded-lg border border-[#e2e8f0] bg-white px-2 py-1.5 pl-5 text-sm focus:outline-none focus:ring-1 focus:ring-[#2447d9]"
                />
              </>
            )}
          </div>
          <button
            onClick={handleSaveEdit}
            disabled={updateService.isPending || !editName.trim()}
            className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#2447d9] text-white hover:bg-[#1e3a8a] disabled:opacity-50 transition-colors"
            aria-label="Save"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={resetEdit}
            className="flex h-7 w-7 items-center justify-center rounded-lg border border-[#e2e8f0] text-[#64748b] hover:bg-[#f1f5f9] transition-colors"
            aria-label="Cancel"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    );
  }

  if (confirmDelete) {
    return (
      <div className="flex items-center gap-2 py-2 border-b border-[#f1f5f9] last:border-0">
        <span className="flex-1 text-sm text-[#dc2626]">Delete &ldquo;{service.name}&rdquo;?</span>
        <button
          onClick={handleDelete}
          disabled={deleteService.isPending}
          className="rounded-lg bg-[#dc2626] px-3 py-1 text-xs font-semibold text-white hover:bg-[#b91c1c] disabled:opacity-50 transition-colors"
        >
          {deleteService.isPending ? "Deleting…" : "Yes, delete"}
        </button>
        <button
          onClick={() => setConfirmDelete(false)}
          className="rounded-lg border border-[#e2e8f0] px-3 py-1 text-xs font-semibold text-[#64748b] hover:bg-[#f1f5f9] transition-colors"
        >
          Cancel
        </button>
      </div>
    );
  }

  const commBadge = () => {
    if (service.commissionType === "fixed" && service.commissionFixed !== undefined && service.commissionFixed > 0) {
      return <span className="inline-flex items-center gap-1 rounded-full bg-[#eff3ff] px-2 py-0.5 text-xs font-semibold text-[#2447d9]">₱{service.commissionFixed.toLocaleString()} fixed</span>;
    }
    const rate = service.commissionRate ?? 0;
    return rate > 0
      ? <span className="inline-flex items-center gap-1 rounded-full bg-[#eff3ff] px-2 py-0.5 text-xs font-semibold text-[#2447d9]">{rate}% commission</span>
      : <span className="text-xs text-[#94a3b8]">No commission</span>;
  };

  return (
    <div className="flex items-center gap-2 py-2.5 border-b border-[#f1f5f9] last:border-0 group">
      <div className="flex-1 min-w-0 space-y-0.5">
        <p className="text-sm font-medium text-[#0f172a] truncate">{service.name}</p>
        <div className="flex flex-wrap items-center gap-1 sm:hidden">
          {service.serviceAmount !== undefined && (
            <span className="inline-flex items-center rounded-full bg-[#f0fdf4] px-2 py-0.5 text-xs font-semibold text-[#15803d]">
              ₱{service.serviceAmount.toLocaleString("en-PH", { minimumFractionDigits: 2 })}
            </span>
          )}
          {commBadge()}
        </div>
      </div>
      <div className="hidden sm:flex items-center gap-2 shrink-0">
        {service.serviceAmount !== undefined && (
          <span className="inline-flex items-center rounded-full bg-[#f0fdf4] px-2 py-0.5 text-xs font-semibold text-[#15803d]">
            ₱{service.serviceAmount.toLocaleString("en-PH", { minimumFractionDigits: 2 })}
          </span>
        )}
        {commBadge()}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button
          onClick={handleToggleLock}
          disabled={updateService.isPending}
          title={service.lockAmount ? "Amount locked — click to unlock" : "Amount editable — click to lock"}
          className={`flex h-7 w-7 items-center justify-center rounded-lg transition-colors disabled:opacity-50 ${
            service.lockAmount
              ? "bg-[#eff3ff] text-[#2447d9] hover:bg-[#e0e7ff]"
              : "text-[#94a3b8] hover:bg-[#f1f5f9] hover:text-[#64748b]"
          }`}
          aria-label={service.lockAmount ? "Unlock amount" : "Lock amount"}
        >
          {service.lockAmount ? <Lock className="h-3.5 w-3.5" /> : <LockOpen className="h-3.5 w-3.5" />}
        </button>
        <button
          onClick={() => setEditing(true)}
          className="flex h-7 w-7 items-center justify-center rounded-lg text-[#94a3b8] hover:bg-[#f1f5f9] hover:text-[#2447d9] transition-colors"
          aria-label="Edit service"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={() => setConfirmDelete(true)}
          className="flex h-7 w-7 items-center justify-center rounded-lg text-[#94a3b8] hover:bg-[#fef2f2] hover:text-[#dc2626] transition-colors"
          aria-label="Delete service"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

// ── Config Tab ────────────────────────────────────────────────────────────────

function CategoryEditor({
  title,
  description,
  categories,
  onChange,
}: {
  title: string;
  description: string;
  categories: StoreConfigCategory[];
  onChange: (cats: StoreConfigCategory[]) => void;
}) {
  const [newName, setNewName] = useState("");

  const handleAdd = () => {
    const name = newName.trim();
    if (!name) return;
    onChange([...categories, { id: newId(), name, enabled: true }]);
    setNewName("");
  };

  const handleToggle = (id: string) =>
    onChange(categories.map((c) => c.id === id ? { ...c, enabled: !c.enabled } : c));

  const handleRemove = (id: string) =>
    onChange(categories.filter((c) => c.id !== id));

  return (
    <div>
      <p className="text-xs font-semibold text-[#374151] mb-1">{title}</p>
      <p className="text-xs text-[#64748b] mb-2">{description}</p>
      {categories.length > 0 && (
        <div className="rounded-lg border border-[#e2e8f0] bg-white mb-2 divide-y divide-[#f1f5f9]">
          {categories.map((cat) => (
            <div key={cat.id} className="flex items-center gap-2 px-3 py-2">
              <button
                type="button"
                onClick={() => handleToggle(cat.id)}
                className={`flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center transition-colors ${cat.enabled ? "bg-[#2447d9] border-[#2447d9]" : "bg-white border-[#cbd5e1]"}`}
              >
                {cat.enabled && <Check className="h-2.5 w-2.5 text-white" />}
              </button>
              <span className={`text-xs flex-1 ${cat.enabled ? "text-[#0f172a]" : "text-[#94a3b8] line-through"}`}>{cat.name}</span>
              <button type="button" onClick={() => handleRemove(cat.id)} className="text-[#94a3b8] hover:text-[#dc2626] transition-colors">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") handleAdd(); }}
          placeholder={`Add ${title.toLowerCase()} category…`}
          className={`${inputClass} flex-1`}
        />
        <Button onClick={handleAdd} disabled={!newName.trim()} size="sm">
          <Plus className="h-4 w-4" /> Add
        </Button>
      </div>
    </div>
  );
}

function ConfigTab({ businessId }: { businessId: string }) {
  const { data: services = [], isLoading } = useStoreServices(businessId);
  const createService = useCreateStoreService();
  const { data: storeConfig } = useStoreConfig(businessId);
  const saveConfig = useSaveStoreConfig();
  const { toasts: catToasts, toast: showToast, dismissToast: dismissCatToast } = useToast();

  const [expenseCategories, setExpenseCategories] = useState<StoreConfigCategory[]>([]);
  const [incomeCategories, setIncomeCategories] = useState<StoreConfigCategory[]>([]);
  const [catsDirty, setCatsDirty] = useState(false);

  // Sync from server config
  const [prevConfig, setPrevConfig] = useState<typeof storeConfig>(undefined);
  if (storeConfig !== prevConfig) {
    setPrevConfig(storeConfig);
    if (storeConfig) {
      setExpenseCategories(storeConfig.expenseCategories ?? []);
      setIncomeCategories(storeConfig.incomeCategories ?? []);
      setCatsDirty(false);
    }
  }

  const handleExpenseCatsChange = (cats: StoreConfigCategory[]) => {
    setExpenseCategories(cats);
    setCatsDirty(true);
  };

  const handleIncomeCatsChange = (cats: StoreConfigCategory[]) => {
    setIncomeCategories(cats);
    setCatsDirty(true);
  };

  const handleSaveCategories = async () => {
    try {
      await saveConfig.mutateAsync({ storeId: businessId, expenseCategories, incomeCategories });
      setCatsDirty(false);
      showToast.success("Categories saved.");
    } catch {
      showToast.error("Failed to save categories.");
    }
  };

  const [newName, setNewName] = useState("");
  const [newAmount, setNewAmount] = useState("");
  const [newCommType, setNewCommType] = useState<"percentage" | "fixed">("percentage");
  const [newRate, setNewRate] = useState("0");
  const [newFixed, setNewFixed] = useState("0");
  const [addError, setAddError] = useState("");

  const handleAdd = async () => {
    if (!newName.trim()) { setAddError("Service name is required."); return; }
    const payload: Parameters<typeof createService.mutateAsync>[0] = {
      storeId: businessId,
      name: newName.trim(),
      commissionType: newCommType,
    };
    if (newAmount !== "") {
      const amt = parseFloat(newAmount);
      if (isNaN(amt) || amt < 0) { setAddError("Service price must be 0 or greater."); return; }
      payload.serviceAmount = amt;
    }
    if (newCommType === "percentage") {
      const rate = parseFloat(newRate);
      if (isNaN(rate) || rate < 0 || rate > 100) { setAddError("Commission rate must be between 0 and 100."); return; }
      payload.commissionRate = rate;
    } else {
      const fixed = parseFloat(newFixed);
      if (isNaN(fixed) || fixed < 0) { setAddError("Fixed commission amount must be 0 or greater."); return; }
      payload.commissionFixed = fixed;
    }
    setAddError("");
    try {
      await createService.mutateAsync(payload);
      setNewName("");
      setNewAmount("");
      setNewRate("0");
      setNewFixed("0");
    } catch {
      setAddError("Failed to add service. Please try again.");
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <div className="mb-3">
          <h2 className="text-sm font-semibold text-[#0f172a]">Service Catalog</h2>
          <p className="text-xs text-[#64748b] mt-0.5">
            Configure the services available for recording in this store. Set a commission rate to automatically credit staff wallets when recording services.
          </p>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-8"><LoadingSpinner /></div>
        ) : services.length === 0 ? (
          <div className="rounded-lg border border-dashed border-[#e2e8f0] py-8 text-center my-2">
            <Tags className="h-7 w-7 text-[#94a3b8] mx-auto mb-2" />
            <p className="text-sm font-medium text-[#374151]">No services yet</p>
            <p className="text-xs text-[#94a3b8] mt-1">Add your first service below.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-2">
              <div className="hidden sm:grid sm:grid-cols-[1fr_140px_56px] gap-2 w-full px-1">
                <span className="text-xs font-semibold text-[#94a3b8] uppercase tracking-wide">Service Name</span>
                <span className="text-xs font-semibold text-[#94a3b8] uppercase tracking-wide">Commission</span>
                <span />
              </div>
            </div>
            <div className="rounded-lg border border-[#e2e8f0] bg-white px-3">
              {services.map((s) => (
                <ServiceRow key={s.serviceId} service={s} storeId={businessId} />
              ))}
            </div>
          </>
        )}

        <div className="mt-5 rounded-lg bg-[#f8fafc] border border-[#e2e8f0] p-4 space-y-2">
          <p className="text-xs font-semibold text-[#374151]">Add New Service</p>
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleAdd(); }}
            placeholder="e.g. Hair Coloring, Facial, Massage…"
            className={inputClass}
          />
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[#94a3b8]">₱</span>
            <input
              type="number" min="0" step="0.01"
              value={newAmount}
              onChange={(e) => setNewAmount(e.target.value)}
              placeholder="Service price (e.g. 500.00)"
              className={`${inputClass} pl-6`}
            />
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <select
              value={newCommType}
              onChange={(e) => setNewCommType(e.target.value as "percentage" | "fixed")}
              className={inputClass}
            >
              <option value="percentage">% Commission</option>
              <option value="fixed">Fixed ₱</option>
            </select>
            <div className="relative flex-1 min-w-[80px]">
              {newCommType === "percentage" ? (
                <>
                  <input
                    type="number" min="0" max="100" step="0.5"
                    value={newRate}
                    onChange={(e) => setNewRate(e.target.value)}
                    placeholder="0"
                    className={`${inputClass} pr-7`}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#94a3b8]">%</span>
                </>
              ) : (
                <>
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[#94a3b8]">₱</span>
                  <input
                    type="number" min="0" step="0.01"
                    value={newFixed}
                    onChange={(e) => setNewFixed(e.target.value)}
                    placeholder="0.00"
                    className={`${inputClass} pl-6`}
                  />
                </>
              )}
            </div>
            <Button onClick={handleAdd} disabled={createService.isPending || !newName.trim()} size="sm">
              <Plus className="h-4 w-4" /> Add
            </Button>
          </div>
          {addError && <p className="text-xs text-[#dc2626]">{addError}</p>}
        </div>
      </Card>

      <Card>
        <div className="mb-3">
          <h2 className="text-sm font-semibold text-[#0f172a]">Expense &amp; Income Categories</h2>
          <p className="text-xs text-[#64748b] mt-0.5">
            Configure dropdown categories for expenses and income entries. Disable a category to hide it without deleting it.
          </p>
        </div>
        <div className="space-y-5">
          <CategoryEditor
            title="Expense Categories"
            description="Shown in the category dropdown when adding or editing an expense."
            categories={expenseCategories}
            onChange={handleExpenseCatsChange}
          />
          <CategoryEditor
            title="Income Categories"
            description="Shown in the category dropdown when adding or editing an income entry."
            categories={incomeCategories}
            onChange={handleIncomeCatsChange}
          />
        </div>
        {catsDirty && (
          <div className="mt-4 flex justify-end">
            <Button onClick={handleSaveCategories} disabled={saveConfig.isPending} size="sm">
              {saveConfig.isPending ? "Saving…" : "Save Categories"}
            </Button>
          </div>
        )}
        <ToastContainer toasts={catToasts} onDismiss={dismissCatToast} />
      </Card>
    </div>
  );
}

// ── Add Record Modal ──────────────────────────────────────────────────────────

function AddRecordModal({ storeId, bizId, onClose }: { storeId: string; bizId: string; onClose: () => void }) {
  const { toasts, toast, dismissToast } = useToast();
  const createRecord = useCreateStoreRecord();
  const { data: staff = [] } = useStoreStaff(storeId);
  const { data: services = [], isLoading: servicesLoading } = useStoreServices(storeId);

  const [date, setDate] = useState(todayIso());
  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [price, setPrice] = useState("");
  const [selectedStaffIds, setSelectedStaffIds] = useState<string[]>([]);
  const [addStaffId, setAddStaffId] = useState("");
  const [notes, setNotes] = useState("");

  const selectedService = services.find((s) => s.serviceId === selectedServiceId);

  const handleServiceChange = (serviceId: string) => {
    setSelectedServiceId(serviceId);
    const svc = services.find((s) => s.serviceId === serviceId);
    if (svc?.serviceAmount !== undefined && svc.serviceAmount > 0) {
      setPrice(String(svc.serviceAmount));
    } else {
      setPrice("");
    }
  };

  const handleAddStaff = () => {
    if (!addStaffId || selectedStaffIds.includes(addStaffId)) return;
    setSelectedStaffIds((prev) => [...prev, addStaffId]);
    setAddStaffId("");
  };

  const handleRemoveStaff = (userId: string) => {
    setSelectedStaffIds((prev) => prev.filter((id) => id !== userId));
  };

  const commissionType = selectedService?.commissionType ?? "percentage";
  const commissionFixed = selectedService?.commissionFixed ?? 0;
  const commRate = selectedService?.commissionRate ?? 0;
  const priceNum = parseFloat(price) || 0;
  const totalComm = commissionType === "fixed"
    ? commissionFixed
    : commRate > 0 ? priceNum * commRate / 100 : 0;
  const perStaffComm = selectedStaffIds.length > 0 ? totalComm / selectedStaffIds.length : 0;
  const hasCommission = totalComm > 0 && selectedStaffIds.length > 0
    && (commissionType === "fixed" || priceNum > 0);

  const handleSave = async () => {
    if (!date) { toast.error("Date is required."); return; }
    if (!selectedServiceId || !selectedService) { toast.error("Service is required."); return; }
    if (price === "" || isNaN(parseFloat(price)) || parseFloat(price) < 0) {
      toast.error("Enter a valid price (0 or greater)."); return;
    }
    if (selectedStaffIds.length === 0) { toast.error("Please select at least one staff member."); return; }

    const primaryStaff = staff.find((s) => s.userId === selectedStaffIds[0]);
    if (!primaryStaff) { toast.error("Staff member not found."); return; }

    const additional = selectedStaffIds.slice(1).map((id) => {
      const s = staff.find((m) => m.userId === id)!;
      return { staffId: id, staffName: `${s.firstName} ${s.lastName}`.trim() };
    });

    try {
      const created = await createRecord.mutateAsync({
        storeId,
        date,
        serviceName: selectedService.name,
        serviceId: selectedService.serviceId,
        price: parseFloat(price),
        staffId: selectedStaffIds[0],
        staffName: `${primaryStaff.firstName} ${primaryStaff.lastName}`.trim(),
        additionalStaff: additional.length > 0 ? additional : undefined,
        notes: notes || undefined,
      });
      void auditApi.log(bizId, {
        entityType: "service-record",
        entityId: created.recordId,
        action: "create-service-record",
        page: "store:records",
        metadata: {
          service: selectedService.name,
          date,
          price: parseFloat(price),
          staff: selectedStaffIds.map((id) => {
            const s = staff.find((m) => m.userId === id);
            return s ? `${s.firstName} ${s.lastName}`.trim() : id;
          }),
          notes: notes || undefined,
        },
      });
      toast.success("Service record saved.");
      setTimeout(onClose, 800);
    } catch {
      toast.error("Failed to save record. Please try again.");
    }
  };

  return (
    <>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Date</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputClass} />
        </div>

        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Service</label>
          {servicesLoading ? (
            <div className="flex items-center gap-2 py-2 text-xs text-[#94a3b8]">
              <LoadingSpinner size="sm" /> Loading services…
            </div>
          ) : services.length === 0 ? (
            <div className="rounded-lg border border-dashed border-[#e2e8f0] px-3 py-3 text-center">
              <p className="text-xs text-[#94a3b8]">
                No services configured yet. Go to the <strong>Configuration</strong> tab to add services.
              </p>
            </div>
          ) : (
            <>
              <select value={selectedServiceId} onChange={(e) => handleServiceChange(e.target.value)} className={inputClass}>
                <option value="">Select service…</option>
                {services.map((s) => (
                  <option key={s.serviceId} value={s.serviceId}>{s.name}{s.serviceAmount ? ` — ₱${s.serviceAmount.toLocaleString("en-PH")}` : ""}</option>
                ))}
              </select>
            </>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Performed By</label>
          {staff.length === 0 ? (
            <p className="text-xs text-[#94a3b8] py-2">
              No active employees found. Add employees in Manage Users first.
            </p>
          ) : (
            <div className="space-y-2">
              <div className="flex gap-2">
                <select
                  value={addStaffId}
                  onChange={(e) => setAddStaffId(e.target.value)}
                  className={`${inputClass} flex-1`}
                >
                  <option value="">Add staff member…</option>
                  {staff
                    .filter((s) => !selectedStaffIds.includes(s.userId))
                    .map((s) => (
                      <option key={s.userId} value={s.userId}>
                        {s.firstName} {s.lastName} — {s.position}
                      </option>
                    ))}
                </select>
                <Button
                  onClick={handleAddStaff}
                  disabled={!addStaffId}
                  size="sm"
                  className="shrink-0"
                >
                  <Plus className="h-3.5 w-3.5" /> Add
                </Button>
              </div>
              {selectedStaffIds.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {selectedStaffIds.map((id) => {
                    const s = staff.find((m) => m.userId === id);
                    if (!s) return null;
                    return (
                      <span
                        key={id}
                        className="inline-flex items-center gap-1 rounded-full bg-[#eff3ff] px-2.5 py-1 text-xs font-medium text-[#2447d9]"
                      >
                        {s.firstName} {s.lastName}
                        <button
                          onClick={() => handleRemoveStaff(id)}
                          className="ml-0.5 rounded-full hover:bg-[#c7d2fe] transition-colors"
                          aria-label={`Remove ${s.firstName}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Price (₱)</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#94a3b8]">₱</span>
            <input type="number" min="0" step="0.01" value={price}
              onChange={(e) => setPrice(e.target.value)} placeholder="0.00"
              className={`${inputClass} pl-7`} />
          </div>
          {selectedService?.serviceAmount !== undefined && (
            <p className="mt-1 text-xs text-[#64748b]">Auto-filled from service — you can override it.</p>
          )}
          {hasCommission && (
            <p className="mt-1 text-xs text-[#2447d9]">
              {commissionType === "fixed"
                ? `Fixed ₱${commissionFixed.toLocaleString("en-PH", { minimumFractionDigits: 2 })} commission`
                : `${commRate}% commission = ₱${totalComm.toLocaleString("en-PH", { minimumFractionDigits: 2 })} total`}
              {selectedStaffIds.length > 1
                ? ` → ₱${perStaffComm.toLocaleString("en-PH", { minimumFractionDigits: 2 })} each (÷ ${selectedStaffIds.length} staff)`
                : ` → ₱${perStaffComm.toLocaleString("en-PH", { minimumFractionDigits: 2 })} credited`}
            </p>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Notes (optional)</label>
          <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="Any additional notes…" className={inputClass} />
        </div>

        <div className="flex gap-3 pt-1">
          <Button variant="secondary" onClick={onClose} fullWidth>Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={createRecord.isPending || services.length === 0}
            fullWidth
          >
            {createRecord.isPending ? "Saving…" : "Save Record"}
          </Button>
        </div>
      </div>
    </>
  );
}

// ── Add Expense Modal ─────────────────────────────────────────────────────────

function AddExpenseModal({ storeId, bizId, onClose }: { storeId: string; bizId: string; onClose: () => void }) {
  const { toasts, toast, dismissToast } = useToast();
  const createExpense = useCreateStoreExpense();
  const { data: storeConfig } = useStoreConfig(storeId);
  const enabledExpCats = (storeConfig?.expenseCategories ?? []).filter((c) => c.enabled);

  const [date, setDate] = useState(todayIso());
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("");

  const handleSave = async () => {
    if (!date) { toast.error("Date is required."); return; }
    if (!description.trim()) { toast.error("Description is required."); return; }
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) { toast.error("Enter a valid amount."); return; }

    try {
      const created = await createExpense.mutateAsync({
        storeId,
        date,
        description: description.trim(),
        amount: amt,
        category: category.trim() || undefined,
      });
      void auditApi.log(bizId, {
        entityType: "expense",
        entityId: created.expenseId,
        action: "create-expense",
        page: "store:expenses",
        metadata: { description: description.trim(), amount: amt, date, category: category.trim() || undefined },
      });
      toast.success("Expense recorded.");
      setTimeout(onClose, 800);
    } catch {
      toast.error("Failed to save expense. Please try again.");
    }
  };

  return (
    <>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Date</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputClass} />
        </div>

        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Description</label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Supplies, Utilities, Rent…"
            className={inputClass}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Amount (₱)</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#94a3b8]">₱</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className={`${inputClass} pl-7`}
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Category (optional)</label>
          {enabledExpCats.length > 0 ? (
            <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputClass}>
              <option value="">— Select category —</option>
              {enabledExpCats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          ) : (
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="e.g. Supplies, Utilities…"
              className={inputClass}
            />
          )}
        </div>

        <div className="flex gap-3 pt-1">
          <Button variant="secondary" onClick={onClose} fullWidth>Cancel</Button>
          <Button onClick={handleSave} disabled={createExpense.isPending} fullWidth>
            {createExpense.isPending ? "Saving…" : "Save Expense"}
          </Button>
        </div>
      </div>
    </>
  );
}

// ── Edit Expense Modal ────────────────────────────────────────────────────────

function EditExpenseModal({ expense, bizId, onClose }: { expense: StoreExpense; bizId: string; onClose: () => void }) {
  const { toasts, toast, dismissToast } = useToast();
  const updateExpense = useUpdateStoreExpense();
  const { data: storeConfig } = useStoreConfig(expense.storeId);
  const enabledExpCats = (storeConfig?.expenseCategories ?? []).filter((c) => c.enabled);
  const [description, setDescription] = useState(expense.description);
  const [amount, setAmount] = useState(String(expense.amount));
  const [category, setCategory] = useState(expense.category ?? "");

  const handleSave = async () => {
    if (!description.trim()) { toast.error("Description is required."); return; }
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) { toast.error("Enter a valid amount."); return; }
    try {
      await updateExpense.mutateAsync({
        expenseId: expense.expenseId,
        payload: {
          storeId: expense.storeId,
          date: expense.date,
          description: description.trim(),
          amount: amt,
          category: category.trim() || undefined,
        },
      });
      const changes: Record<string, { before?: unknown; after?: unknown }> = {};
      if (description.trim() !== expense.description) changes.description = { before: expense.description, after: description.trim() };
      if (amt !== expense.amount) changes.amount = { before: expense.amount, after: amt };
      if ((category.trim() || undefined) !== expense.category) changes.category = { before: expense.category, after: category.trim() || undefined };
      void auditApi.log(bizId, {
        entityType: "expense",
        entityId: expense.expenseId,
        action: "update-expense",
        page: "store:expenses",
        changes,
      });
      toast.success("Expense updated.");
      setTimeout(onClose, 800);
    } catch {
      toast.error("Failed to update expense. Please try again.");
    }
  };

  return (
    <>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      <div className="space-y-4">
        <div className="rounded-lg bg-[#f8fafc] border border-[#e2e8f0] px-4 py-2.5 text-xs text-[#64748b]">
          Date: <span className="font-medium text-[#0f172a]">{expense.date}</span>
        </div>
        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Description</label>
          <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} className={inputClass} />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Amount (₱)</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#94a3b8]">₱</span>
            <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className={`${inputClass} pl-7`} />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Category (optional)</label>
          {enabledExpCats.length > 0 ? (
            <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputClass}>
              <option value="">— Select category —</option>
              {enabledExpCats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          ) : (
            <input type="text" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. Supplies, Utilities…" className={inputClass} />
          )}
        </div>
        <div className="flex gap-3 pt-1">
          <Button variant="secondary" onClick={onClose} fullWidth>Cancel</Button>
          <Button onClick={handleSave} disabled={updateExpense.isPending} fullWidth>
            {updateExpense.isPending ? "Saving…" : "Save Changes"}
          </Button>
        </div>
      </div>
    </>
  );
}

// ── Add Income Modal ──────────────────────────────────────────────────────────

function AddIncomeModal({ storeId, bizId, onClose }: { storeId: string; bizId: string; onClose: () => void }) {
  const { toasts, toast, dismissToast } = useToast();
  const createIncome = useCreateStoreIncome();
  const { data: storeConfig } = useStoreConfig(storeId);
  const enabledIncCats = (storeConfig?.incomeCategories ?? []).filter((c) => c.enabled);

  const [date, setDate] = useState(todayIso());
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("");

  const handleSave = async () => {
    if (!date) { toast.error("Date is required."); return; }
    if (!description.trim()) { toast.error("Description is required."); return; }
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) { toast.error("Enter a valid amount."); return; }

    try {
      const created = await createIncome.mutateAsync({
        storeId,
        date,
        description: description.trim(),
        amount: amt,
        category: category.trim() || undefined,
      });
      void auditApi.log(bizId, {
        entityType: "income",
        entityId: created.incomeId,
        action: "create-income",
        page: "store:income",
        metadata: { description: description.trim(), amount: amt, date, category: category.trim() || undefined },
      });
      toast.success("Income recorded.");
      setTimeout(onClose, 800);
    } catch {
      toast.error("Failed to save income. Please try again.");
    }
  };

  return (
    <>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Date</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputClass} />
        </div>

        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Description</label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Walk-in sale, Product sale…"
            className={inputClass}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Amount (₱)</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#94a3b8]">₱</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className={`${inputClass} pl-7`}
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Category (optional)</label>
          {enabledIncCats.length > 0 ? (
            <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputClass}>
              <option value="">— Select category —</option>
              {enabledIncCats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          ) : (
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="e.g. Product sales, Tips…"
              className={inputClass}
            />
          )}
        </div>

        <div className="flex gap-3 pt-1">
          <Button variant="secondary" onClick={onClose} fullWidth>Cancel</Button>
          <Button onClick={handleSave} disabled={createIncome.isPending} fullWidth>
            {createIncome.isPending ? "Saving…" : "Save Income"}
          </Button>
        </div>
      </div>
    </>
  );
}

// ── Edit Income Modal ─────────────────────────────────────────────────────────

function EditIncomeModal({ income, bizId, onClose }: { income: StoreIncome; bizId: string; onClose: () => void }) {
  const { toasts, toast, dismissToast } = useToast();
  const updateIncome = useUpdateStoreIncome();
  const { data: storeConfig } = useStoreConfig(income.storeId);
  const enabledIncCats = (storeConfig?.incomeCategories ?? []).filter((c) => c.enabled);
  const [description, setDescription] = useState(income.description);
  const [amount, setAmount] = useState(String(income.amount));
  const [category, setCategory] = useState(income.category ?? "");

  const handleSave = async () => {
    if (!description.trim()) { toast.error("Description is required."); return; }
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) { toast.error("Enter a valid amount."); return; }
    try {
      await updateIncome.mutateAsync({
        incomeId: income.incomeId,
        payload: {
          storeId: income.storeId,
          date: income.date,
          description: description.trim(),
          amount: amt,
          category: category.trim() || undefined,
        },
      });
      const changes: Record<string, { before?: unknown; after?: unknown }> = {};
      if (description.trim() !== income.description) changes.description = { before: income.description, after: description.trim() };
      if (amt !== income.amount) changes.amount = { before: income.amount, after: amt };
      if ((category.trim() || undefined) !== income.category) changes.category = { before: income.category, after: category.trim() || undefined };
      void auditApi.log(bizId, {
        entityType: "income",
        entityId: income.incomeId,
        action: "update-income",
        page: "store:income",
        changes,
      });
      toast.success("Income updated.");
      setTimeout(onClose, 800);
    } catch {
      toast.error("Failed to update income. Please try again.");
    }
  };

  return (
    <>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      <div className="space-y-4">
        <div className="rounded-lg bg-[#f8fafc] border border-[#e2e8f0] px-4 py-2.5 text-xs text-[#64748b]">
          Date: <span className="font-medium text-[#0f172a]">{income.date}</span>
        </div>
        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Description</label>
          <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} className={inputClass} />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Amount (₱)</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#94a3b8]">₱</span>
            <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className={`${inputClass} pl-7`} />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Category (optional)</label>
          {enabledIncCats.length > 0 ? (
            <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputClass}>
              <option value="">— Select category —</option>
              {enabledIncCats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          ) : (
            <input type="text" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. Product sales, Tips…" className={inputClass} />
          )}
        </div>
        <div className="flex gap-3 pt-1">
          <Button variant="secondary" onClick={onClose} fullWidth>Cancel</Button>
          <Button onClick={handleSave} disabled={updateIncome.isPending} fullWidth>
            {updateIncome.isPending ? "Saving…" : "Save Changes"}
          </Button>
        </div>
      </div>
    </>
  );
}

// ── Edit Record Modal ─────────────────────────────────────────────────────────

function EditRecordModal({ record, storeId, bizId, onClose }: { record: StoreRecord; storeId: string; bizId: string; onClose: () => void }) {
  const { toasts, toast, dismissToast } = useToast();
  const updateRecord = useUpdateStoreRecord();
  const { data: staff = [] } = useStoreStaff(storeId);

  const [price, setPrice] = useState(String(record.price));
  const [selectedStaffIds, setSelectedStaffIds] = useState<string[]>(() => [
    record.staffId,
    ...(record.additionalStaff ?? []).map((s) => s.staffId),
  ]);
  const [addStaffId, setAddStaffId] = useState("");
  const [notes, setNotes] = useState(record.notes ?? "");

  const handleAddStaff = () => {
    if (!addStaffId || selectedStaffIds.includes(addStaffId)) return;
    setSelectedStaffIds((prev) => [...prev, addStaffId]);
    setAddStaffId("");
  };

  const handleRemoveStaff = (userId: string) => {
    setSelectedStaffIds((prev) => prev.filter((id) => id !== userId));
  };

  const handleSave = async () => {
    const parsed = parseFloat(price);
    if (isNaN(parsed) || parsed < 0) { toast.error("Enter a valid price (0 or greater)."); return; }
    if (selectedStaffIds.length === 0) { toast.error("Please select at least one staff member."); return; }
    const primaryStaff = staff.find((s) => s.userId === selectedStaffIds[0]);
    if (!primaryStaff) { toast.error("Staff member not found."); return; }

    const additional = selectedStaffIds.slice(1).map((id) => {
      const s = staff.find((m) => m.userId === id)!;
      return { staffId: id, staffName: `${s.firstName} ${s.lastName}`.trim() };
    });

    try {
      const oldStaff = [record.staffId, ...(record.additionalStaff ?? []).map((s) => s.staffId)];
      await updateRecord.mutateAsync({
        recordId: record.recordId,
        payload: {
          storeId,
          date: record.date,
          price: parsed,
          staffId: selectedStaffIds[0],
          staffName: `${primaryStaff.firstName} ${primaryStaff.lastName}`.trim(),
          additionalStaff: additional.length > 0 ? additional : [],
          notes: notes || undefined,
        },
      });
      const changes: Record<string, { before?: unknown; after?: unknown }> = {};
      if (parsed !== record.price) changes.price = { before: record.price, after: parsed };
      if (JSON.stringify(selectedStaffIds) !== JSON.stringify(oldStaff)) {
        changes.staff = {
          before: [record.staffName, ...(record.additionalStaff ?? []).map((s) => s.staffName)],
          after: selectedStaffIds.map((id) => {
            const s = staff.find((m) => m.userId === id);
            return s ? `${s.firstName} ${s.lastName}`.trim() : id;
          }),
        };
      }
      if ((notes || "") !== (record.notes ?? "")) changes.notes = { before: record.notes ?? "", after: notes };
      void auditApi.log(bizId, {
        entityType: "service-record",
        entityId: record.recordId,
        action: "update-service-record",
        page: "store:records",
        changes: Object.keys(changes).length > 0 ? changes : undefined,
        metadata: { service: record.serviceName, date: record.date },
      });
      toast.success("Record updated.");
      setTimeout(onClose, 800);
    } catch {
      toast.error("Failed to update record. Please try again.");
    }
  };

  return (
    <>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      <div className="space-y-4">
        <div className="rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2 text-sm text-[#64748b]">
          <span className="font-medium text-[#0f172a]">{record.serviceName}</span> · {record.date}
        </div>

        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Performed By</label>
          {selectedStaffIds.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {selectedStaffIds.map((id) => {
                const s = staff.find((m) => m.userId === id);
                const name = s ? `${s.firstName} ${s.lastName}`.trim() : id;
                return (
                  <span key={id} className="inline-flex items-center gap-1 rounded-full bg-[#eff3ff] px-2.5 py-1 text-xs font-medium text-[#2447d9]">
                    {name}
                    {selectedStaffIds.length > 1 && (
                      <button onClick={() => handleRemoveStaff(id)} className="ml-0.5 text-[#748ffc] hover:text-[#2447d9]">
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </span>
                );
              })}
            </div>
          )}
          {staff.length === 0 ? (
            <p className="text-xs text-[#94a3b8] py-2">No staff found.</p>
          ) : (
            <div className="flex gap-2">
              <select value={addStaffId} onChange={(e) => setAddStaffId(e.target.value)} className={`${inputClass} flex-1`}>
                <option value="">Add staff member…</option>
                {staff.filter((s) => !selectedStaffIds.includes(s.userId)).map((s) => (
                  <option key={s.userId} value={s.userId}>
                    {s.firstName} {s.lastName} — {s.position}
                  </option>
                ))}
              </select>
              <button onClick={handleAddStaff} disabled={!addStaffId}
                className="rounded-lg border border-[#e2e8f0] px-3 py-2 text-xs font-medium text-[#2447d9] hover:bg-[#eff3ff] disabled:opacity-40 transition-colors">
                Add
              </button>
            </div>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Price (₱)</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#94a3b8]">₱</span>
            <input type="number" min="0" step="0.01" value={price}
              onChange={(e) => setPrice(e.target.value)} placeholder="0.00"
              className={`${inputClass} pl-7`} />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">Notes (optional)</label>
          <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="Any additional notes…" className={inputClass} />
        </div>

        <div className="flex gap-3 pt-1">
          <Button variant="secondary" onClick={onClose} fullWidth>Cancel</Button>
          <Button onClick={handleSave} disabled={updateRecord.isPending} fullWidth>
            {updateRecord.isPending ? "Saving…" : "Save Changes"}
          </Button>
        </div>
      </div>
    </>
  );
}

// ── Dashboard Tab ─────────────────────────────────────────────────────────────

type ViewMode = "daily" | "monthly" | "yearly" | "custom";

function computeCommission(record: StoreRecord, services: StoreService[]): number {
  const svc = services.find((s) => s.serviceId === record.serviceId);
  if (!svc) return 0;
  if (svc.commissionType === "fixed") return svc.commissionFixed ?? 0;
  const rate = svc.commissionRate ?? 0;
  return rate > 0 ? (record.price * rate) / 100 : 0;
}

function Dashboard({ storeId }: { storeId: string }) {
  const [viewMode, setViewMode] = useState<ViewMode>("monthly");
  const [customStart, setCustomStart] = useState(() => currentMonth() + "-01");
  const [customEnd, setCustomEnd] = useState(todayIso);

  // For custom mode, fetch the year of the start date; filter client-side by range.
  const fetchPrefix = viewMode === "daily" ? todayIso()
    : viewMode === "monthly" ? currentMonth()
    : viewMode === "yearly" ? currentYear()
    : customStart.slice(0, 4);

  const { data: rawRecords = [], isLoading: recordsLoading } = useStoreRecords(storeId, fetchPrefix);
  const { data: approvedRequests = [], isLoading: reqLoading } = useAdminServiceRequests(storeId, "APPROVED");
  const { data: services = [] } = useStoreServices(storeId);
  // Current (all-time) per-staff wallet balances — independent of the period filter.
  const { data: walletBalances = [] } = useStaffWalletBalances(storeId);

  // For custom mode, filter records to [customStart, customEnd].
  const records = useMemo(() => {
    if (viewMode !== "custom") return rawRecords;
    return rawRecords.filter((r) => r.date >= customStart && r.date <= customEnd);
  }, [rawRecords, viewMode, customStart, customEnd]);

  const isLoading = recordsLoading || reqLoading;

  const displayPrefix = viewMode === "custom" ? `${customStart} – ${customEnd}` : fetchPrefix;

  // Filter approved service requests to the selected time window.
  const filteredRequests = useMemo(() => {
    if (viewMode === "custom") {
      return approvedRequests.filter((r) => {
        const d = (r.createdAt ?? "").slice(0, 10);
        return d >= customStart && d <= customEnd;
      });
    }
    return approvedRequests.filter((r) => (r.createdAt ?? "").startsWith(fetchPrefix));
  }, [approvedRequests, viewMode, fetchPrefix, customStart, customEnd]);

  const totalIncome = records.reduce((s, r) => s + r.price, 0)
    + filteredRequests.reduce((s, r) => s + r.amount, 0);
  const totalServices = records.length + filteredRequests.length;
  const avgPrice = totalServices > 0 ? totalIncome / totalServices : 0;

  const byService = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of records) map.set(r.serviceName, (map.get(r.serviceName) ?? 0) + r.price);
    for (const r of filteredRequests) map.set(r.description, (map.get(r.description) ?? 0) + r.amount);
    return Array.from(map.entries()).map(([name, total]) => ({ name, total })).sort((a, b) => b.total - a.total);
  }, [records, filteredRequests]);

  const byStaff = useMemo(() => {
    const map = new Map<string, { name: string; total: number; commission: number; count: number }>();
    for (const r of records) {
      // Credit every staff on the record (primary + additional). Price and commission
      // are split equally across them — matching how the wallet splits commission —
      // so multi-staff services attribute correctly to each participant.
      const allStaff = [
        { staffId: r.staffId, staffName: r.staffName },
        ...(r.additionalStaff ?? []),
      ];
      if (allStaff.length === 0) continue;
      const totalComm = computeCommission(r, services);
      const commPerStaff = totalComm / allStaff.length;
      const pricePerStaff = r.price / allStaff.length;
      for (const st of allStaff) {
        const ex = map.get(st.staffId) ?? { name: st.staffName, total: 0, commission: 0, count: 0 };
        map.set(st.staffId, {
          name: st.staffName,
          total: ex.total + pricePerStaff,
          commission: ex.commission + commPerStaff,
          count: ex.count + 1,
        });
      }
    }
    for (const r of filteredRequests) {
      const key = r.userId;
      const ex = map.get(key) ?? { name: r.employeeName ?? key, total: 0, commission: 0, count: 0 };
      map.set(key, { name: r.employeeName ?? key, total: ex.total + r.amount, commission: ex.commission, count: ex.count + 1 });
    }
    return Array.from(map.values()).sort((a, b) => b.total - a.total).slice(0, 5);
  }, [records, filteredRequests, services]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1 rounded-lg border border-[#e2e8f0] bg-white p-1 w-fit">
          {(["daily", "monthly", "yearly", "custom"] as ViewMode[]).map((mode) => (
            <button key={mode} onClick={() => setViewMode(mode)}
              className={`rounded-md px-4 py-1.5 text-xs font-semibold capitalize transition-colors ${
                viewMode === mode ? "bg-[#2447d9] text-white" : "text-[#64748b] hover:text-[#0f172a]"
              }`}>{mode}</button>
          ))}
        </div>
        {viewMode === "custom" && (
          <div className="flex items-center gap-2">
            <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)}
              className="rounded-lg border border-[#e2e8f0] bg-white px-3 py-1.5 text-xs focus:border-[#2447d9] focus:outline-none focus:ring-1 focus:ring-[#2447d9]" />
            <span className="text-xs text-[#64748b]">to</span>
            <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)}
              min={customStart}
              className="rounded-lg border border-[#e2e8f0] bg-white px-3 py-1.5 text-xs focus:border-[#2447d9] focus:outline-none focus:ring-1 focus:ring-[#2447d9]" />
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12"><LoadingSpinner /></div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {[
              { label: "Total Income", value: formatCurrency(totalIncome), icon: <TrendingUp className="h-5 w-5" />, color: "text-[#2447d9] bg-[#eff3ff]" },
              { label: "Services Rendered", value: String(totalServices), icon: <Scissors className="h-5 w-5" />, color: "text-[#16a34a] bg-[#f0fdf4]" },
              { label: "Average per Service", value: formatCurrency(avgPrice), icon: <BarChart2 className="h-5 w-5" />, color: "text-[#d97706] bg-[#fffbeb]" },
            ].map((k) => (
              <div key={k.label} className="rounded-xl border border-[#e2e8f0] bg-white px-5 py-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${k.color}`}>{k.icon}</div>
                  <p className="text-xs font-medium text-[#64748b]">{k.label}</p>
                </div>
                <p className="text-2xl font-bold text-[#0f172a]">{k.value}</p>
                <p className="text-xs text-[#94a3b8] mt-0.5">{viewMode === "custom" ? displayPrefix : formatPrefixLabel(fetchPrefix)}</p>
              </div>
            ))}
          </div>

          {totalServices === 0 ? (
            <EmptyState icon={<Scissors className="h-8 w-8 text-[#94a3b8]" />}
              title="No records for this period" description="Add service records to see the dashboard." />
          ) : (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <Card title="Income by Service">
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={byService} margin={{ top: 4, right: 4, left: 0, bottom: 4 }}>
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₱${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v: number) => formatCurrency(v)} />
                    <Bar dataKey="total" radius={[4, 4, 0, 0]}>
                      {byService.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Card>

              <Card title="Top Staff by Income">
                {byStaff.length === 0 ? (
                  <p className="text-sm text-[#94a3b8] text-center py-8">No data</p>
                ) : (
                  <div className="space-y-3">
                    {byStaff.map((s, i) => {
                      const pct = totalIncome > 0 ? (s.total / totalIncome) * 100 : 0;
                      return (
                        <div key={i}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="font-medium text-[#0f172a]">{s.name}</span>
                            <div className="text-right">
                              <span className="text-[#64748b]">{formatCurrency(s.total)} · {s.count} svc</span>
                              {s.commission > 0 && (
                                <p className="text-xs text-[#16a34a]">Commission: {formatCurrency(s.commission)}</p>
                              )}
                            </div>
                          </div>
                          <div className="h-2 rounded-full bg-[#e2e8f0] overflow-hidden">
                            <div className="h-full rounded-full"
                              style={{ width: `${pct}%`, backgroundColor: COLORS[i % COLORS.length] }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>
            </div>
          )}

          {/* Staff wallet balances — current (all-time), not period-scoped */}
          <Card title="Staff Wallet Balances">
            <p className="text-xs text-[#94a3b8] -mt-2 mb-3">
              Current totals per staff (same figures the employee sees) — independent of the date filter above.
            </p>
            {walletBalances.filter((b) => b.totalEarned > 0 || b.availableBalance !== 0).length === 0 ? (
              <p className="text-sm text-[#94a3b8] text-center py-6">No staff earnings yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-[#64748b] uppercase tracking-wide">Staff</th>
                      <th className="px-4 py-2.5 text-right text-xs font-semibold text-[#64748b] uppercase tracking-wide">Total Earned</th>
                      <th className="px-4 py-2.5 text-right text-xs font-semibold text-[#64748b] uppercase tracking-wide">Available Balance</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#f1f5f9]">
                    {[...walletBalances]
                      .filter((b) => b.totalEarned > 0 || b.availableBalance !== 0)
                      .sort((a, b) => b.availableBalance - a.availableBalance)
                      .map((b) => (
                        <tr key={b.userId} className="hover:bg-[#f8fafc]">
                          <td className="px-4 py-3 font-medium text-[#0f172a]">{b.name}</td>
                          <td className="px-4 py-3 text-right text-[#64748b]">{formatCurrency(b.totalEarned)}</td>
                          <td className="px-4 py-3 text-right font-semibold text-[#16a34a]">{formatCurrency(b.availableBalance)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

// ── Records Tab ───────────────────────────────────────────────────────────────

function RecordsTab({ storeId, bizId }: { storeId: string; bizId: string }) {
  const [prefix, setPrefix] = useState(currentMonth());
  const [prefixType, setPrefixType] = useState<"day" | "month" | "year">("month");
  const [dayValue, setDayValue] = useState(todayIso());
  const [monthValue, setMonthValue] = useState(currentMonth());
  const [yearValue, setYearValue] = useState(currentYear());
  const [serviceFilter, setServiceFilter] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [editRecord, setEditRecord] = useState<StoreRecord | null>(null);
  const [historyRecord, setHistoryRecord] = useState<StoreRecord | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const deleteRecord = useDeleteStoreRecord();

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useStoreRecordsInfinite(storeId, prefix, undefined, serviceFilter || undefined);

  const { data: services = [] } = useStoreServices(storeId);

  const records = useMemo(
    () => data?.pages.flatMap((p) => p.items) ?? [],
    [data],
  );

  // totalPrice and totalCount come from the first page (computed server-side for the full filter set)
  const totalPrice = data?.pages[0]?.totalPrice;
  const totalCount = data?.pages[0]?.totalCount;

  const handleLoad = () => {
    const p = prefixType === "day" ? dayValue : prefixType === "month" ? monthValue : yearValue;
    setPrefix(p);
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const all = await storeApi.exportRecords(storeId, prefix, { serviceId: serviceFilter || undefined });
      const header = ["Date", "Service", "Staff", "Additional Staff", "Price", "Notes", "Recorded By", "Created At"];
      const rows = all.map((r) => [
        r.date,
        r.serviceName,
        r.staffName,
        (r.additionalStaff ?? []).map((s) => s.staffName).join(" | "),
        r.price,
        r.notes ?? "",
        r.createdBy,
        r.createdAt,
      ]);
      const csv = [header, ...rows]
        .map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
        .join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `records-${prefix}${serviceFilter ? `-svc` : ""}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex flex-wrap items-end gap-3">
          {/* Date range selector */}
          <div className="flex items-center gap-1 rounded-lg border border-[#e2e8f0] p-1">
            {(["day", "month", "year"] as const).map((t) => (
              <button key={t} onClick={() => setPrefixType(t)}
                className={`rounded-md px-3 py-1 text-xs font-medium capitalize transition-colors ${prefixType === t ? "bg-[#2447d9] text-white" : "text-[#64748b]"}`}>
                {t}
              </button>
            ))}
          </div>

          {prefixType === "day" && (
            <input type="date" value={dayValue} onChange={(e) => setDayValue(e.target.value)}
              className="rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2447d9]" />
          )}
          {prefixType === "month" && (
            <input type="month" value={monthValue} onChange={(e) => setMonthValue(e.target.value)}
              className="rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2447d9]" />
          )}
          {prefixType === "year" && (
            <input type="number" value={yearValue} onChange={(e) => setYearValue(e.target.value)}
              min="2020" max="2099" placeholder="YYYY"
              className="w-28 rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2447d9]" />
          )}

          {/* Service filter */}
          <select
            value={serviceFilter}
            onChange={(e) => setServiceFilter(e.target.value)}
            className="rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2447d9]"
          >
            <option value="">All services</option>
            {services.map((s) => (
              <option key={s.serviceId} value={s.serviceId}>{s.name}</option>
            ))}
          </select>

          <Button onClick={handleLoad} variant="secondary" size="sm">Load</Button>
          <Button onClick={() => setShowAdd(true)}>
            <Plus className="h-4 w-4" /> Add Record
          </Button>
          <button
            onClick={handleExport}
            disabled={isExporting || !totalCount}
            className="flex items-center gap-1.5 rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm font-medium text-[#64748b] hover:bg-[#f1f5f9] hover:text-[#0f172a] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Export to CSV"
          >
            <Download className="h-4 w-4" />
            {isExporting ? "Exporting…" : "Export CSV"}
          </button>
        </div>
      </Card>

      <Card padding="none">
        <div className="px-5 py-3 border-b border-[#e2e8f0] flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-[#0f172a]">{formatPrefixLabel(prefix)}</h2>
            {totalCount !== undefined && totalCount > 0 && (
              <p className="text-xs text-[#64748b] mt-0.5">
                {totalCount} records · Total: {formatCurrency(totalPrice ?? 0)}
                {records.length < totalCount && (
                  <span className="ml-1 text-[#94a3b8]">(showing {records.length})</span>
                )}
              </p>
            )}
          </div>
          <button onClick={() => refetch()} className="text-[#94a3b8] hover:text-[#64748b] transition-colors" title="Refresh">
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-10"><LoadingSpinner /></div>
        ) : records.length === 0 ? (
          <EmptyState icon={<Scissors className="h-7 w-7 text-[#94a3b8]" />}
            title="No records for this period"
            description="Click 'Add Record' to log a service."
            className="py-10" />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
                    {["Date", "Service", "Staff", "Price", "Commission", "Notes", "Recorded By", ""].map((h) => (
                      <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#f1f5f9]">
                  {records.map((r) => {
                    const allStaff = [
                      { staffId: r.staffId, staffName: r.staffName },
                      ...(r.additionalStaff ?? []),
                    ];
                    const comm = computeCommission(r, services);
                    const perStaff = allStaff.length > 0 ? comm / allStaff.length : 0;
                    return (
                      <tr key={r.recordId} className="hover:bg-[#f8fafc]">
                        <td className="px-5 py-3 text-[#64748b] whitespace-nowrap">{r.date}</td>
                        <td className="px-5 py-3 font-medium text-[#0f172a]">{r.serviceName}</td>
                        <td className="px-5 py-3 text-[#64748b]">
                          <div className="flex flex-col gap-0.5">
                            {allStaff.map((s) => (
                              <span key={s.staffId} className="whitespace-nowrap">
                                {s.staffName}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-5 py-3 font-semibold text-[#2447d9]">{formatCurrency(r.price)}</td>
                        <td className="px-5 py-3">
                          {comm > 0 ? (
                            <div className="flex flex-col gap-0.5">
                              <span className="text-xs font-medium text-[#16a34a]">{formatCurrency(comm)} total</span>
                              {allStaff.length > 1 && (
                                <span className="text-[10px] text-[#64748b]">{formatCurrency(perStaff)} / staff</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-[#94a3b8]">—</span>
                          )}
                        </td>
                        <td className="px-5 py-3 text-[#94a3b8] max-w-[180px] truncate">{r.notes ?? "—"}</td>
                        <td className="px-5 py-3 whitespace-nowrap">
                          <p className="text-xs font-medium text-[#374151]">{r.createdBy}</p>
                          <p className="text-[10px] text-[#94a3b8]">{formatDate(r.createdAt)}</p>
                        </td>
                        <td className="px-3 py-3">
                          {confirmDeleteId === r.recordId ? (
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-[#dc2626]">Delete?</span>
                              <button
                                onClick={async () => {
                                  await deleteRecord.mutateAsync({ recordId: r.recordId, storeId: r.storeId, date: r.date });
                                  void auditApi.log(bizId, {
                                    entityType: "service-record",
                                    entityId: r.recordId,
                                    action: "delete-service-record",
                                    page: "store:records",
                                    metadata: {
                                      service: r.serviceName,
                                      date: r.date,
                                      price: r.price,
                                      staff: [r.staffName, ...(r.additionalStaff ?? []).map((s) => s.staffName)],
                                    },
                                  });
                                  setConfirmDeleteId(null);
                                  refetch();
                                }}
                                disabled={deleteRecord.isPending}
                                className="rounded bg-[#dc2626] px-2 py-0.5 text-xs text-white disabled:opacity-60"
                              >Yes</button>
                              <button onClick={() => setConfirmDeleteId(null)} className="rounded border border-[#e2e8f0] px-2 py-0.5 text-xs text-[#64748b]">No</button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => setHistoryRecord(r)}
                                className="rounded-lg border border-[#e2e8f0] p-1.5 text-[#64748b] hover:bg-[#f1f5f9] hover:text-[#0f172a] transition-colors"
                                title="View change history"
                              >
                                <History className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => setEditRecord(r)}
                                className="rounded-lg border border-[#e2e8f0] p-1.5 text-[#64748b] hover:bg-[#f1f5f9] hover:text-[#0f172a] transition-colors"
                                title="Edit record"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => setConfirmDeleteId(r.recordId)}
                                className="rounded-lg border border-[#fecaca] p-1.5 text-[#dc2626] hover:bg-[#fee2e2] transition-colors"
                                title="Delete record"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {hasNextPage && (
              <div className="px-5 py-3 border-t border-[#e2e8f0] flex items-center justify-center">
                <button
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className="text-sm font-medium text-[#2447d9] hover:text-[#1a35a8] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isFetchingNextPage ? "Loading…" : "Load more"}
                </button>
              </div>
            )}
          </>
        )}
      </Card>

      {showAdd && (
        <Modal isOpen onClose={() => setShowAdd(false)} title="Add Service Record" size="sm">
          <AddRecordModal storeId={storeId} bizId={bizId} onClose={() => { setShowAdd(false); refetch(); }} />
        </Modal>
      )}

      {editRecord && (
        <Modal isOpen onClose={() => setEditRecord(null)} title="Edit Service Record" size="sm">
          <EditRecordModal record={editRecord} storeId={storeId} bizId={bizId} onClose={() => { setEditRecord(null); refetch(); }} />
        </Modal>
      )}

      {historyRecord && (
        <AuditHistoryModal
          isOpen
          onClose={() => setHistoryRecord(null)}
          businessId={bizId}
          entityType="service-record"
          entityId={historyRecord.recordId}
          label={`${historyRecord.serviceName} · ${historyRecord.date}`}
        />
      )}
    </div>
  );
}

// ── Expenses Tab ──────────────────────────────────────────────────────────────

function ExpensesTab({ storeId, bizId }: { storeId: string; bizId: string }) {
  const [prefix, setPrefix] = useState(currentMonth());
  const [prefixType, setPrefixType] = useState<"day" | "month" | "year">("month");
  const [dayValue, setDayValue] = useState(todayIso());
  const [monthValue, setMonthValue] = useState(currentMonth());
  const [yearValue, setYearValue] = useState(currentYear());
  const [categoryFilter, setCategoryFilter] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [editExpense, setEditExpense] = useState<StoreExpense | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [historyExpense, setHistoryExpense] = useState<StoreExpense | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const deleteExpense = useDeleteStoreExpense();
  const { data: storeConfig } = useStoreConfig(storeId);

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, refetch } =
    useStoreExpensesInfinite(storeId, prefix, categoryFilter || undefined);

  const expenses = useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data]);
  const totalAmount = data?.pages[0]?.totalAmount;
  const totalCount = data?.pages[0]?.totalCount;

  const handleLoad = () => {
    const p = prefixType === "day" ? dayValue : prefixType === "month" ? monthValue : yearValue;
    setPrefix(p);
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const all = await storeApi.exportExpenses(storeId, prefix, { category: categoryFilter || undefined });
      const header = ["Date", "Description", "Category", "Amount", "Recorded By", "Created At"];
      const rows = all.map((e) => [e.date, e.description, e.category ?? "", e.amount, e.createdBy, e.createdAt]);
      const csv = [header, ...rows]
        .map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
        .join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `expenses-${prefix}${categoryFilter ? `-cat` : ""}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-1 rounded-lg border border-[#e2e8f0] p-1">
            {(["day", "month", "year"] as const).map((t) => (
              <button key={t} onClick={() => setPrefixType(t)}
                className={`rounded-md px-3 py-1 text-xs font-medium capitalize transition-colors ${prefixType === t ? "bg-[#2447d9] text-white" : "text-[#64748b]"}`}>
                {t}
              </button>
            ))}
          </div>
          {prefixType === "day" && (
            <input type="date" value={dayValue} onChange={(e) => setDayValue(e.target.value)}
              className="rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2447d9]" />
          )}
          {prefixType === "month" && (
            <input type="month" value={monthValue} onChange={(e) => setMonthValue(e.target.value)}
              className="rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2447d9]" />
          )}
          {prefixType === "year" && (
            <input type="number" value={yearValue} onChange={(e) => setYearValue(e.target.value)}
              min="2020" max="2099" placeholder="YYYY"
              className="w-28 rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2447d9]" />
          )}
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
            className="rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2447d9]">
            <option value="">All categories</option>
            {(storeConfig?.expenseCategories ?? []).filter((c) => c.enabled).map((c) => (
              <option key={c.id} value={c.name}>{c.name}</option>
            ))}
          </select>
          <Button onClick={handleLoad} variant="secondary" size="sm">Load</Button>
          <Button onClick={() => setShowAdd(true)}>
            <Plus className="h-4 w-4" /> Add Expense
          </Button>
          <button
            onClick={handleExport}
            disabled={isExporting || !totalCount}
            className="flex items-center gap-1.5 rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm font-medium text-[#64748b] hover:bg-[#f1f5f9] hover:text-[#0f172a] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Download className="h-4 w-4" />
            {isExporting ? "Exporting…" : "Export CSV"}
          </button>
        </div>
      </Card>

      <Card padding="none">
        <div className="px-5 py-3 border-b border-[#e2e8f0] flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-[#0f172a]">Expenses — {formatPrefixLabel(prefix)}</h2>
            {totalCount !== undefined && totalCount > 0 && (
              <p className="text-xs text-[#64748b] mt-0.5">
                {totalCount} expense{totalCount !== 1 ? "s" : ""} · Total: {formatCurrency(totalAmount ?? 0)}
                {expenses.length < totalCount && <span className="ml-1 text-[#94a3b8]">(showing {expenses.length})</span>}
              </p>
            )}
          </div>
          <button onClick={() => refetch()} className="text-[#94a3b8] hover:text-[#64748b] transition-colors" title="Refresh">
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-10"><LoadingSpinner /></div>
        ) : expenses.length === 0 ? (
          <EmptyState icon={<TrendingDown className="h-7 w-7 text-[#94a3b8]" />}
            title="No expenses for this period"
            description="Click 'Add Expense' to log an expense."
            className="py-10" />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
                    {["Date", "Description", "Category", "Amount", "Recorded By", ""].map((h) => (
                      <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#f1f5f9]">
                  {expenses.map((e) => (
                    <tr key={e.expenseId} className="hover:bg-[#f8fafc]">
                      <td className="px-5 py-3 text-[#64748b] whitespace-nowrap">{e.date}</td>
                      <td className="px-5 py-3 font-medium text-[#0f172a]">{e.description}</td>
                      <td className="px-5 py-3 text-[#64748b]">{e.category ?? "—"}</td>
                      <td className="px-5 py-3 font-semibold text-[#dc2626]">{formatCurrency(e.amount)}</td>
                      <td className="px-5 py-3">
                        <p className="text-xs font-medium text-[#374151]">{e.createdBy}</p>
                        <p className="text-[10px] text-[#94a3b8]">{formatDate(e.createdAt)}</p>
                      </td>
                      <td className="px-3 py-3">
                        {confirmDeleteId === e.expenseId ? (
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-[#dc2626]">Delete?</span>
                            <button
                              onClick={async () => {
                                await deleteExpense.mutateAsync({ expenseId: e.expenseId, storeId: e.storeId, date: e.date });
                                void auditApi.log(bizId, {
                                  entityType: "expense",
                                  entityId: e.expenseId,
                                  action: "delete-expense",
                                  page: "store:expenses",
                                  metadata: { description: e.description, amount: e.amount, date: e.date },
                                });
                                setConfirmDeleteId(null);
                                refetch();
                              }}
                              disabled={deleteExpense.isPending}
                              className="rounded bg-[#dc2626] px-2 py-0.5 text-xs text-white disabled:opacity-60"
                            >Yes</button>
                            <button onClick={() => setConfirmDeleteId(null)} className="rounded border border-[#e2e8f0] px-2 py-0.5 text-xs text-[#64748b]">No</button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            <button onClick={() => setEditExpense(e)}
                              className="rounded-lg border border-[#e2e8f0] p-1.5 text-[#64748b] hover:bg-[#f1f5f9] hover:text-[#0f172a] transition-colors" title="Edit expense">
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button onClick={() => setConfirmDeleteId(e.expenseId)}
                              className="rounded-lg border border-[#fecaca] p-1.5 text-[#dc2626] hover:bg-[#fee2e2] transition-colors" title="Delete expense">
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                            <button onClick={() => setHistoryExpense(e)}
                              className="rounded-lg border border-[#e2e8f0] p-1.5 text-[#64748b] hover:bg-[#f1f5f9] hover:text-[#0f172a] transition-colors" title="View history">
                              <History className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {hasNextPage && (
              <div className="px-5 py-3 border-t border-[#e2e8f0] flex items-center justify-center">
                <button onClick={() => fetchNextPage()} disabled={isFetchingNextPage}
                  className="text-sm font-medium text-[#2447d9] hover:text-[#1a35a8] disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  {isFetchingNextPage ? "Loading…" : "Load more"}
                </button>
              </div>
            )}
          </>
        )}
      </Card>

      {showAdd && (
        <Modal isOpen onClose={() => setShowAdd(false)} title="Add Expense" size="sm">
          <AddExpenseModal storeId={storeId} bizId={bizId} onClose={() => { setShowAdd(false); refetch(); }} />
        </Modal>
      )}

      {editExpense && (
        <Modal isOpen onClose={() => setEditExpense(null)} title="Edit Expense" size="sm">
          <EditExpenseModal expense={editExpense} bizId={bizId} onClose={() => { setEditExpense(null); refetch(); }} />
        </Modal>
      )}

      {historyExpense && (
        <AuditHistoryModal
          isOpen
          onClose={() => setHistoryExpense(null)}
          businessId={bizId}
          entityType="expense"
          entityId={historyExpense.expenseId}
          label={`${historyExpense.description} · ${historyExpense.date}`}
        />
      )}
    </div>
  );
}

// ── Income Tab ────────────────────────────────────────────────────────────────

function IncomeTab({ storeId, bizId }: { storeId: string; bizId: string }) {
  const [prefix, setPrefix] = useState(currentMonth());
  const [prefixType, setPrefixType] = useState<"day" | "month" | "year">("month");
  const [dayValue, setDayValue] = useState(todayIso());
  const [monthValue, setMonthValue] = useState(currentMonth());
  const [yearValue, setYearValue] = useState(currentYear());
  const [categoryFilter, setCategoryFilter] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [editIncome, setEditIncome] = useState<StoreIncome | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [historyIncome, setHistoryIncome] = useState<StoreIncome | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const deleteIncome = useDeleteStoreIncome();
  const { data: storeConfig } = useStoreConfig(storeId);

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, refetch } =
    useStoreIncomeInfinite(storeId, prefix, categoryFilter || undefined);

  const income = useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data]);
  const totalAmount = data?.pages[0]?.totalAmount;
  const totalCount = data?.pages[0]?.totalCount;

  const handleLoad = () => {
    const p = prefixType === "day" ? dayValue : prefixType === "month" ? monthValue : yearValue;
    setPrefix(p);
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const all = await storeApi.exportIncome(storeId, prefix, { category: categoryFilter || undefined });
      const header = ["Date", "Description", "Category", "Amount", "Recorded By", "Created At"];
      const rows = all.map((e) => [e.date, e.description, e.category ?? "", e.amount, e.createdBy, e.createdAt]);
      const csv = [header, ...rows]
        .map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
        .join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `income-${prefix}${categoryFilter ? `-cat` : ""}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-1 rounded-lg border border-[#e2e8f0] p-1">
            {(["day", "month", "year"] as const).map((t) => (
              <button key={t} onClick={() => setPrefixType(t)}
                className={`rounded-md px-3 py-1 text-xs font-medium capitalize transition-colors ${prefixType === t ? "bg-[#2447d9] text-white" : "text-[#64748b]"}`}>
                {t}
              </button>
            ))}
          </div>
          {prefixType === "day" && (
            <input type="date" value={dayValue} onChange={(e) => setDayValue(e.target.value)}
              className="rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2447d9]" />
          )}
          {prefixType === "month" && (
            <input type="month" value={monthValue} onChange={(e) => setMonthValue(e.target.value)}
              className="rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2447d9]" />
          )}
          {prefixType === "year" && (
            <input type="number" value={yearValue} onChange={(e) => setYearValue(e.target.value)}
              min="2020" max="2099" placeholder="YYYY"
              className="w-28 rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2447d9]" />
          )}
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
            className="rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2447d9]">
            <option value="">All categories</option>
            {(storeConfig?.incomeCategories ?? []).filter((c) => c.enabled).map((c) => (
              <option key={c.id} value={c.name}>{c.name}</option>
            ))}
          </select>
          <Button onClick={handleLoad} variant="secondary" size="sm">Load</Button>
          <Button onClick={() => setShowAdd(true)}>
            <Plus className="h-4 w-4" /> Add Income
          </Button>
          <button
            onClick={handleExport}
            disabled={isExporting || !totalCount}
            className="flex items-center gap-1.5 rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm font-medium text-[#64748b] hover:bg-[#f1f5f9] hover:text-[#0f172a] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Download className="h-4 w-4" />
            {isExporting ? "Exporting…" : "Export CSV"}
          </button>
        </div>
      </Card>

      <Card padding="none">
        <div className="px-5 py-3 border-b border-[#e2e8f0] flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-[#0f172a]">Income — {formatPrefixLabel(prefix)}</h2>
            {totalCount !== undefined && totalCount > 0 && (
              <p className="text-xs text-[#64748b] mt-0.5">
                {totalCount} entr{totalCount !== 1 ? "ies" : "y"} · Total: {formatCurrency(totalAmount ?? 0)}
                {income.length < totalCount && <span className="ml-1 text-[#94a3b8]">(showing {income.length})</span>}
              </p>
            )}
          </div>
          <button onClick={() => refetch()} className="text-[#94a3b8] hover:text-[#64748b] transition-colors" title="Refresh">
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-10"><LoadingSpinner /></div>
        ) : income.length === 0 ? (
          <EmptyState icon={<TrendingUp className="h-7 w-7 text-[#94a3b8]" />}
            title="No income for this period"
            description="Click 'Add Income' to log an income entry."
            className="py-10" />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
                    {["Date", "Description", "Category", "Amount", "Recorded By", ""].map((h) => (
                      <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#f1f5f9]">
                  {income.map((e) => (
                    <tr key={e.incomeId} className="hover:bg-[#f8fafc]">
                      <td className="px-5 py-3 text-[#64748b] whitespace-nowrap">{e.date}</td>
                      <td className="px-5 py-3 font-medium text-[#0f172a]">{e.description}</td>
                      <td className="px-5 py-3 text-[#64748b]">{e.category ?? "—"}</td>
                      <td className="px-5 py-3 font-semibold text-[#16a34a]">{formatCurrency(e.amount)}</td>
                      <td className="px-5 py-3">
                        <p className="text-xs font-medium text-[#374151]">{e.createdBy}</p>
                        <p className="text-[10px] text-[#94a3b8]">{formatDate(e.createdAt)}</p>
                      </td>
                      <td className="px-3 py-3">
                        {confirmDeleteId === e.incomeId ? (
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-[#dc2626]">Delete?</span>
                            <button
                              onClick={async () => {
                                await deleteIncome.mutateAsync({ incomeId: e.incomeId, storeId: e.storeId, date: e.date });
                                void auditApi.log(bizId, {
                                  entityType: "income",
                                  entityId: e.incomeId,
                                  action: "delete-income",
                                  page: "store:income",
                                  metadata: { description: e.description, amount: e.amount, date: e.date },
                                });
                                setConfirmDeleteId(null);
                                refetch();
                              }}
                              disabled={deleteIncome.isPending}
                              className="rounded bg-[#dc2626] px-2 py-0.5 text-xs text-white disabled:opacity-60"
                            >Yes</button>
                            <button onClick={() => setConfirmDeleteId(null)} className="rounded border border-[#e2e8f0] px-2 py-0.5 text-xs text-[#64748b]">No</button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            <button onClick={() => setEditIncome(e)}
                              className="rounded-lg border border-[#e2e8f0] p-1.5 text-[#64748b] hover:bg-[#f1f5f9] hover:text-[#0f172a] transition-colors" title="Edit income">
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button onClick={() => setConfirmDeleteId(e.incomeId)}
                              className="rounded-lg border border-[#fecaca] p-1.5 text-[#dc2626] hover:bg-[#fee2e2] transition-colors" title="Delete income">
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                            <button onClick={() => setHistoryIncome(e)}
                              className="rounded-lg border border-[#e2e8f0] p-1.5 text-[#64748b] hover:bg-[#f1f5f9] hover:text-[#0f172a] transition-colors" title="View history">
                              <History className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {hasNextPage && (
              <div className="px-5 py-3 border-t border-[#e2e8f0] flex items-center justify-center">
                <button onClick={() => fetchNextPage()} disabled={isFetchingNextPage}
                  className="text-sm font-medium text-[#2447d9] hover:text-[#1a35a8] disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  {isFetchingNextPage ? "Loading…" : "Load more"}
                </button>
              </div>
            )}
          </>
        )}
      </Card>

      {showAdd && (
        <Modal isOpen onClose={() => setShowAdd(false)} title="Add Income" size="sm">
          <AddIncomeModal storeId={storeId} bizId={bizId} onClose={() => { setShowAdd(false); refetch(); }} />
        </Modal>
      )}

      {editIncome && (
        <Modal isOpen onClose={() => setEditIncome(null)} title="Edit Income" size="sm">
          <EditIncomeModal income={editIncome} bizId={bizId} onClose={() => { setEditIncome(null); refetch(); }} />
        </Modal>
      )}

      {historyIncome && (
        <AuditHistoryModal
          isOpen
          onClose={() => setHistoryIncome(null)}
          businessId={bizId}
          entityType="income"
          entityId={historyIncome.incomeId}
          label={`${historyIncome.description} · ${historyIncome.date}`}
        />
      )}
    </div>
  );
}

// ── Service Requests Tab ──────────────────────────────────────────────────────

function ServiceRequestsTab({ businessId }: { businessId: string }) {
  const [statusFilter, setStatusFilter] = useState<"PENDING" | "APPROVED" | "REJECTED">("PENDING");
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [commissionInput, setCommissionInput] = useState("");
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data: requests = [], refetch } = useAdminServiceRequests(businessId, statusFilter);
  const { data: services = [] } = useStoreServices(businessId);
  const approve = useApproveServiceRequest();
  const reject = useRejectServiceRequest();

  const calcAutoCommission = (req: ServiceRequestItem): string => {
    if (!req.serviceId) return "";
    const svc = services.find((s) => s.serviceId === req.serviceId);
    if (!svc) return "";
    if (svc.commissionType === "fixed" && svc.commissionFixed) return String(svc.commissionFixed);
    const rate = svc.commissionRate ?? 0;
    if (rate > 0) return String(Math.round(req.amount * rate / 100 * 100) / 100);
    return "";
  };

  const handleStartApproving = (req: ServiceRequestItem) => {
    setApprovingId(req.requestId);
    setRejectingId(null);
    setCommissionInput(calcAutoCommission(req));
  };

  const handleApprove = async (req: ServiceRequestItem) => {
    const amt = parseFloat(commissionInput);
    if (!amt || amt <= 0) return;
    await approve.mutateAsync({
      requestId: req.requestId,
      payload: { userId: req.userId, businessId, commissionAmount: amt },
    });
    setApprovingId(null);
    setCommissionInput("");
    refetch();
  };

  const handleReject = async (req: ServiceRequestItem) => {
    await reject.mutateAsync({
      requestId: req.requestId,
      payload: { userId: req.userId, businessId },
    });
    setRejectingId(null);
    refetch();
  };

  const statusColors: Record<string, string> = {
    PENDING: "bg-[#fef9c3] text-[#a16207]",
    APPROVED: "bg-[#dcfce7] text-[#16a34a]",
    REJECTED: "bg-[#fee2e2] text-[#dc2626]",
  };

  return (
    <div className="space-y-4">
      {/* Status filter */}
      <div className="flex gap-2">
        {(["PENDING", "APPROVED", "REJECTED"] as const).map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              statusFilter === s ? "bg-[#2447d9] text-white" : "bg-[#f1f5f9] text-[#64748b] hover:bg-[#e2e8f0]"
            }`}
          >
            {s.charAt(0) + s.slice(1).toLowerCase()}
          </button>
        ))}
      </div>

      {requests.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-[#e2e8f0] bg-white py-12 text-center">
          <p className="text-sm text-[#64748b]">No {statusFilter.toLowerCase()} service requests.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-[#e2e8f0] bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#f1f5f9] bg-[#f8fafc]">
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#94a3b8] uppercase tracking-wide">Employee</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#94a3b8] uppercase tracking-wide">Description</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-[#94a3b8] uppercase tracking-wide">Amount</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-[#94a3b8] uppercase tracking-wide">Commission</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#94a3b8] uppercase tracking-wide">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#94a3b8] uppercase tracking-wide">Submitted</th>
                {statusFilter === "PENDING" && (
                  <th className="px-4 py-3 text-right text-xs font-semibold text-[#94a3b8] uppercase tracking-wide">Actions</th>
                )}
                <th className="w-6" />
              </tr>
            </thead>
            <tbody>
              {requests.map((req) => (
                <>
                  <tr
                    key={req.requestId}
                    className="border-b border-[#f1f5f9] last:border-0 hover:bg-[#f8fafc] cursor-pointer"
                    onClick={() => setExpandedId(expandedId === req.requestId ? null : req.requestId)}
                  >
                    <td className="px-4 py-3 text-[#0f172a] font-medium">{req.employeeName ?? req.userId}</td>
                    <td className="px-4 py-3 text-[#374151] max-w-[200px] truncate">{req.description}</td>
                    <td className="px-4 py-3 text-right text-[#0f172a]">{formatCurrency(req.amount)}</td>
                    <td className="px-4 py-3 text-right text-[#16a34a] font-medium">
                      {req.commissionAmount != null ? formatCurrency(req.commissionAmount) : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${statusColors[req.status]}`}>
                        {req.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[#64748b] text-xs">{formatDate(req.createdAt)}</td>
                    {statusFilter === "PENDING" && (
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        {approvingId === req.requestId ? (
                          <div className="flex items-center gap-1.5 justify-end">
                            <input
                              type="number"
                              placeholder="Commission ₱"
                              value={commissionInput}
                              onChange={(e) => setCommissionInput(e.target.value)}
                              className="w-28 rounded-md border border-[#e2e8f0] px-2 py-1 text-xs focus:border-[#2447d9] focus:outline-none"
                              autoFocus
                            />
                            <button
                              onClick={() => handleApprove(req)}
                              disabled={approve.isPending}
                              className="rounded-md bg-[#2447d9] px-2 py-1 text-xs text-white hover:bg-[#1e3a8a] disabled:opacity-60"
                            >
                              <Check className="h-3.5 w-3.5" />
                            </button>
                            <button onClick={() => setApprovingId(null)} className="rounded-md border border-[#e2e8f0] px-2 py-1 text-xs text-[#64748b]">
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ) : rejectingId === req.requestId ? (
                          <div className="flex items-center gap-1.5 justify-end">
                            <span className="text-xs text-[#dc2626]">Reject?</span>
                            <button
                              onClick={() => handleReject(req)}
                              disabled={reject.isPending}
                              className="rounded-md bg-[#dc2626] px-2 py-1 text-xs text-white disabled:opacity-60"
                            >
                              Confirm
                            </button>
                            <button onClick={() => setRejectingId(null)} className="rounded-md border border-[#e2e8f0] px-2 py-1 text-xs text-[#64748b]">
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5 justify-end">
                            <button
                              onClick={() => handleStartApproving(req)}
                              className="rounded-md bg-[#dcfce7] px-2.5 py-1 text-xs font-medium text-[#16a34a] hover:bg-[#bbf7d0]"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => { setRejectingId(req.requestId); setApprovingId(null); }}
                              className="rounded-md bg-[#fee2e2] px-2.5 py-1 text-xs font-medium text-[#dc2626] hover:bg-[#fecaca]"
                            >
                              Reject
                            </button>
                          </div>
                        )}
                      </td>
                    )}
                    <td className="px-2 py-3 text-[#94a3b8]">
                      {expandedId === req.requestId ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </td>
                  </tr>
                  {expandedId === req.requestId && (
                    <tr key={`${req.requestId}-expanded`} className="bg-[#f8fafc]">
                      <td colSpan={statusFilter === "PENDING" ? 8 : 7} className="px-4 pb-3 pt-1">
                        <div className="text-xs space-y-1 text-[#64748b]">
                          {req.notes && <p><span className="font-medium">Notes:</span> {req.notes}</p>}
                          {req.reviewedAt && <p><span className="font-medium">Reviewed:</span> {formatDate(req.reviewedAt)} by {req.reviewedBy}</p>}
                          {req.reviewNotes && <p><span className="font-medium">Admin note:</span> {req.reviewNotes}</p>}
                          {!req.notes && !req.reviewedAt && !req.reviewNotes && <p className="text-[#94a3b8] italic">No additional details.</p>}
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

type Tab = "records" | "expenses" | "income" | "dashboard" | "config" | "service-requests";

export default function ManageStorePage() {
  const { isAdmin, isActiveBusinessAdmin, activeBusinessId, hasPageAccess } = useAuth();
  const canViewConfig = isAdmin || isActiveBusinessAdmin || hasPageAccess("store:manage-store");
  const [tab, setTab] = useState<Tab>("records");

  const tabs = [
    { key: "records" as Tab, label: "Service Records", icon: <Calendar className="h-4 w-4" /> },
    { key: "expenses" as Tab, label: "Expenses", icon: <TrendingDown className="h-4 w-4" /> },
    { key: "income" as Tab, label: "Income", icon: <Receipt className="h-4 w-4" /> },
    { key: "dashboard" as Tab, label: "Income Dashboard", icon: <BarChart2 className="h-4 w-4" /> },
    ...(canViewConfig ? [{ key: "config" as Tab, label: "Configuration", icon: <Settings className="h-4 w-4" /> }] : []),
  ];

  if (!activeBusinessId) {
    return (
      <div className="space-y-5">
        <div className="rounded-xl border border-[#e2e8f0] bg-white px-5 py-4 shadow-sm">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#2447d9]">
              <Scissors className="h-4 w-4 text-white" />
            </div>
            <div>
              <h1 className="text-base font-bold text-[#0f172a]">Manage Store</h1>
              <p className="text-xs text-[#64748b]">Track services and income by date, staff, and service type</p>
            </div>
          </div>
        </div>
        <EmptyState
          icon={<Scissors className="h-8 w-8 text-[#94a3b8]" />}
          title="No active business"
          description="Select a business from the top bar to manage its store."
        />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* ── Header card ───────────────────────────────────────────────────── */}
      <div className="rounded-xl border border-[#e2e8f0] bg-white px-5 py-4 shadow-sm">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#2447d9]">
            <Scissors className="h-4 w-4 text-white" />
          </div>
          <div>
            <h1 className="text-base font-bold text-[#0f172a]">Manage Store</h1>
            <p className="text-xs text-[#64748b]">Track services and income by date, staff, and service type</p>
          </div>
        </div>
      </div>

      {/* ── Tabs ──────────────────────────────────────────────────────────── */}
      <div className="flex gap-1 border-b border-[#e2e8f0] overflow-x-auto overflow-y-hidden scrollbar-none -mx-4 px-4 md:mx-0 md:px-0">
        {tabs.map(({ key, label, icon }) => (
          <button key={key} onClick={() => setTab(key)}
            className={`flex items-center gap-1.5 px-3 py-2.5 text-xs sm:text-sm font-medium border-b-2 transition-colors -mb-px whitespace-nowrap shrink-0 ${
              tab === key ? "border-[#2447d9] text-[#2447d9]" : "border-transparent text-[#64748b] hover:text-[#0f172a]"
            }`}
          >
            {icon}{label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === "config" && canViewConfig ? (
        <ConfigTab businessId={activeBusinessId} />
      ) : tab === "service-requests" ? (
        <ServiceRequestsTab businessId={activeBusinessId} />
      ) : tab === "records" ? (
        <RecordsTab storeId={activeBusinessId} bizId={activeBusinessId} />
      ) : tab === "expenses" ? (
        <ExpensesTab storeId={activeBusinessId} bizId={activeBusinessId} />
      ) : tab === "income" ? (
        <IncomeTab storeId={activeBusinessId} bizId={activeBusinessId} />
      ) : (
        <Dashboard storeId={activeBusinessId} />
      )}
    </div>
  );
}
